import React from "react";

interface PieChartProps {
  percentage?: number; // how much of the pie is filled (0–100)
  size?: number; // diameter of the pie (in px)
  gapDistance?: number; // how far the filled slice is pushed out
}

const PieChart: React.FC<PieChartProps> = ({
  percentage = 25,
  size = 250,
  gapDistance = 20,
}) => {
  const radius = size / 2;
  const fullCircle = 360;
  const fillAngle = (percentage / 100) * fullCircle;

  const toRad = (deg: number) => (Math.PI / 180) * deg;

  // Angles for the filled (yellow) segment
  const startAngle = -90;
  const endAngle = startAngle + fillAngle;
  const midAngle = (startAngle + endAngle) / 2;

  // Offset for the "pushed out" slice
  const offsetX = Math.cos(toRad(midAngle)) * gapDistance;
  const offsetY = Math.sin(toRad(midAngle)) * gapDistance;

  const largeArc = fillAngle > 180 ? 1 : 0;
  const center = radius + gapDistance; // center of the new expanded viewBox

  const coord = (angle: number): [number, number] => [
    center + radius * Math.cos(toRad(angle)),
    center + radius * Math.sin(toRad(angle)),
  ];

  const [x1, y1] = coord(startAngle);
  const [x2, y2] = coord(endAngle);

  // Filled (yellow) wedge path
  const filledPath = `
    M ${center + offsetX} ${center + offsetY}
    L ${x1 + offsetX} ${y1 + offsetY}
    A ${radius} ${radius} 0 ${largeArc} 1 ${x2 + offsetX} ${y2 + offsetY}
    Z
  `;

  // Remaining (striped) wedge path
  const remainingPath = `
    M ${center} ${center}
    L ${x2} ${y2}
    A ${radius} ${radius} 0 ${fillAngle > 180 ? 0 : 1} 1 ${x1} ${y1}
    Z
  `;

  // expand the SVG viewBox to fit the offset slice
  const viewBoxSize = size + gapDistance * 2;

  return (
    <svg
      width={viewBoxSize}
      height={viewBoxSize}
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
    >
      <defs>
        <pattern
          id="stripePattern"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(135)"
        >
          <rect width="5" height="8" fill="#ECEAE5" />
          <rect x="5" width="3" height="8" fill="rgba(0,0,0,0.15)" />
        </pattern>
      </defs>

      {/* striped remaining section */}
      <path d={remainingPath} fill="url(#stripePattern)" />

      {/* yellow filled section (offset outward) */}
      <path d={filledPath} fill="#FFF47E" />
    </svg>
  );
};

export default PieChart;
